/**
 * Simple Toast Notification Service
 *
 * Lightweight wrapper around react-hot-toast for simple, non-persisted notifications.
 * All toasts render through the unified UnifiedToast component via showUnifiedToast().
 *
 * For persistent notifications that store to the database, use:
 *   import { useUnifiedNotifications } from '@/hooks/useUnifiedNotifications';
 *
 * Usage:
 *   import { notify } from '@/lib/notifications';
 *
 *   notify.success('Saved!')
 *   notify.error('Failed to save')
 *   notify.loading('Saving...')
 *   notify.info('FYI...')
 *   notify.warning('Be careful!')
 *   notify.dismiss(toastId)
 *   notify.dismissAll()
 *   notify.message({
 *     sender: 'John Doe',
 *     message: 'New call incoming',
 *     avatar: '/avatars/john.jpg'
 *   })
 */

import toast from 'react-hot-toast';
import { showUnifiedToast } from './show-unified-toast';
import type {
  RichMessageOptions,
  NotifyInstance,
  ToastOptions,
} from './toast-types';

/**
 * Show a success toast
 */
function success(message: string, options?: ToastOptions): string {
  return showUnifiedToast({
    variant: 'success',
    title: message,
    description: options?.description,
    categoryLabel: options?.categoryLabel,
    actionLabel: options?.actionLabel,
    onAction: options?.onAction,
    icon: options?.icon,
    showCloseButton: options?.showCloseButton,
    duration: options?.duration,
    id: options?.id,
    retain: options?.retain,
  });
}

/**
 * Show an error toast
 */
function error(message: string, options?: ToastOptions): string {
  return showUnifiedToast({
    variant: 'error',
    title: message,
    description: options?.description,
    categoryLabel: options?.categoryLabel,
    actionLabel: options?.actionLabel,
    onAction: options?.onAction,
    icon: options?.icon,
    showCloseButton: options?.showCloseButton,
    duration: options?.duration,
    id: options?.id,
    retain: options?.retain,
  });
}

/**
 * Show an info toast
 */
function info(message: string, options?: ToastOptions): string {
  return showUnifiedToast({
    variant: 'info',
    title: message,
    description: options?.description,
    categoryLabel: options?.categoryLabel,
    actionLabel: options?.actionLabel,
    onAction: options?.onAction,
    icon: options?.icon,
    showCloseButton: options?.showCloseButton,
    duration: options?.duration,
    id: options?.id,
    retain: options?.retain,
  });
}

/**
 * Show a warning toast
 */
function warning(message: string, options?: ToastOptions): string {
  return showUnifiedToast({
    variant: 'warning',
    title: message,
    description: options?.description,
    categoryLabel: options?.categoryLabel,
    actionLabel: options?.actionLabel,
    onAction: options?.onAction,
    icon: options?.icon,
    showCloseButton: options?.showCloseButton,
    duration: options?.duration,
    id: options?.id,
    retain: options?.retain,
  });
}

/**
 * Show a loading toast (must be dismissed manually)
 */
function loading(message: string, options?: ToastOptions): string {
  return showUnifiedToast({
    variant: 'loading',
    title: message,
    description: options?.description,
    icon: options?.icon,
    duration: options?.duration,
    id: options?.id,
    retain: options?.retain,
  });
}

/**
 * Show a rich message notification with sender info
 */
function message(options: RichMessageOptions): string {
  const { sender, message: text, id, avatar, onClick, duration } = options;
  const truncatedText = text.length > 80 ? text.substring(0, 80) + '...' : text;

  return showUnifiedToast({
    variant: 'message',
    title: truncatedText,
    coalesceId: id,
    sender,
    avatar,
    onClick: () => {
      if (onClick) onClick();
    },
    duration,
  });
}

/**
 * Dismiss a specific toast by ID
 */
function dismiss(toastId?: string): void {
  if (toastId) {
    toast.dismiss(toastId);
  }
}

/**
 * Dismiss all toasts
 */
function dismissAll(): void {
  toast.dismiss();
}

/**
 * Promise-based toast for async operations
 * Implemented as manual loading → dismiss → success/error since
 * toast.promise() doesn't support custom rendering.
 */
async function promise<T>(
  promiseValue: Promise<T>,
  messages: {
    loading: string;
    success: string | ((data: T) => string);
    error: string | ((err: Error) => string);
  },
  options?: ToastOptions
): Promise<T> {
  const loadingId = showUnifiedToast({
    variant: 'loading',
    title: messages.loading,
    id: options?.id,
  });

  try {
    const result = await promiseValue;
    toast.dismiss(loadingId);
    const successMsg = typeof messages.success === 'function'
      ? messages.success(result)
      : messages.success;
    showUnifiedToast({
      variant: 'success',
      title: successMsg,
      duration: options?.duration,
      retain: options?.retain,
    });
    return result;
  } catch (err) {
    toast.dismiss(loadingId);
    const errorMsg = typeof messages.error === 'function'
      ? messages.error(err as Error)
      : messages.error;
    showUnifiedToast({
      variant: 'error',
      title: errorMsg,
      duration: options?.duration,
      retain: options?.retain, // ignored for failures by rule; passed for uniformity
    });
    throw err;
  }
}

/**
 * Update an existing toast
 */
function update(toastId: string, message: string, type: 'success' | 'error' | 'loading'): void {
  toast.dismiss(toastId);
  showUnifiedToast({
    variant: type,
    title: message,
    id: toastId,
  });
}

/**
 * The unified notify service
 */
export const notify: NotifyInstance = {
  success,
  error,
  info,
  warning,
  loading,
  message,
  dismiss,
  dismissAll,
  promise,
  update,
};

export default notify;
